import React, { useState } from "react";
import { COMPONENT_DATA_LOCAL_KEY } from "features/MappingEditor/consts";
import { COEStore } from "features/ComponentEditor/ComponentEditorStore";
import { useNavigate, useParams } from "react-router";
import { ROUTES_ENUM } from "routes/consts";
import { searchStore } from "features/ProductSearch/SearchStore";
import useToast from "hooks/useToast";
import { useTranslation } from "react-i18next";
import { productSearchStore as store } from "./ProductSearchStore";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import { ProductActionMode } from "utils";

export interface RemoveStateProps {
  open: boolean;
  id?: string | null;
  name?: string | null;
}

const useProductActions = () => {
  const navigate = useNavigate();
  const { ifc_id } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const [openPublishConfirmModal, setOpenPublishConfirmModal] = useState(false);

  const editComponent = async (id?: string | null, isDetails?: boolean) => {
    if (!id) return;
    await searchStore.getComponentDetails(id, ifc_id);
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.COMPONENT_EDITOR}`, {
      state: {
        id: id,
        compliant: false,
        mode: ProductActionMode.Edit,
        isDetails: isDetails,
      },
    });
  };

  const duplicateComponent = async (
    id?: string | null,
    isDetails?: boolean
  ) => {
    if (!id) return;
    await searchStore.getComponentDetails(id, ifc_id);
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.COMPONENT_EDITOR}`, {
      state: {
        id: id,
        compliant: false,
        mode: ProductActionMode.Duplicate,
        isDetails: isDetails,
      },
    });
  };

  function saveStateAndGetPath() {
    if (COEStore.openedSearchInCE) {
      localStorage.setItem(
        COMPONENT_DATA_LOCAL_KEY,
        JSON.stringify({
          data: COEStore.data,
        })
      );
    }
    const pathname = location.pathname
      .replace("/component-editor", "")
      .replace(/\/+$/, "");
    return pathname;
  }

  const removeComponent = async () => {
    if (!store.removeItemState?.id) return;
    const filteredItems = store.searchResults.items.filter(
      (item) => item.id !== store.removeItemState?.id
    );
    await COEStore.deleteComponent(store.removeItemState?.id)
      .then(async () => {
        store.setSearchResults({
          total: store.searchResults.total - 1,
          items: filteredItems,
          count: filteredItems.length,
        });
        toast(t("components.successMsgOnDelete"), "success");
      })
      .catch((err) => {
        toast(err, "warning");
        console.error(err, "componentsDelete");
      });
  };

  const handleChangeProductStatus = async (
    isPublic?: boolean,
    productName?: string | null,
    productID?: string | null
  ) => {
    if (!productID) return;

    if (isPublic && !openPublishConfirmModal) {
      setOpenPublishConfirmModal(true);
      return;
    }

    await store
      .changeProductStatus(productID, isPublic ? "unpublish" : "publish")
      .then(() =>
        toast(
          t(
            `productSearch.${isPublic ? "successUnpublish" : "successPublish"}`,
            { product_name: productName }
          ),
          "success"
        )
      )
      .catch((err) =>
        toast(
          t(`productSearch.${isPublic ? "errorUnpublish" : "errorPublish"}`, {
            product_name: productName,
            error_code: err.message,
          }),
          "warning"
        )
      );
  };

  const removeProduct = async () => {
    if (!store.removeItemState?.id) return;
    const filteredItems = store.searchResults.items.filter(
      (item) => item.id !== store.removeItemState?.id
    );
    await BPEStore.deleteProduct(store.removeItemState?.id)
      .then(async () => {
        store.setSearchResults({
          items: filteredItems,
          count: filteredItems.length,
          total: store.searchResults.total - 1,
        });
        toast(t("productEditor.successMsgOnDelete"), "success");
        store.setRemoveItemState(undefined);
      })
      .catch((err) => {
        toast(err, "warning");
        console.error(err, "ProductEditorDeleteCustomBuildingProduct");
      });
  };

  return {
    editComponent,
    duplicateComponent,
    saveStateAndGetPath,
    removeComponent,
    handleChangeProductStatus,
    openPublishConfirmModal,
    setOpenPublishConfirmModal,
    removeProduct,
  };
};

export default useProductActions;
